import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import LeagueLogo from "@/components/leagues/league-logo";
import TeamLogo from "@/components/team-logo";
import { formatTimestampToLocalTime } from "@/utils/time";
import '../styles/gameComponent.scss';
import { Lineup } from "@/components/FormationField";
import { fetchDataAfterRefresh, useUserStore } from "@/components/login/loginStore";
import SEO from '../components/SEO';
import PremiumGuard from "@/components/premium-guard";
import { RefereeInfo } from "@/types";
import { Icon } from "@iconify/react";
import { fetchData } from "@/utils/requests";
import Field from "@/components/field";
import { useTranslation } from 'react-i18next';
import GamesHeadToHead from "@/components/GamesHeadToHead";
import GamesStats from "@/components/GameStats";

export interface GameInfo {
    id: number;
    name: string;
    home_id: number;
    away_id: number;
    home_name: string;
    away_name: string;
    league_id: number;
    league_name: string;
    starting_at_timestamp: number;
    created_at: number;
    updated_at: number;
    lineups: Lineup[] | null;
    referee_id: number | null;
    score: string | null;
}

interface GameProps {
    game_id?: number | null;
    isCard?: boolean;
    isModal?: boolean;
}

export function getFormations(players: Lineup[], homeId: number, awayId: number) {
    let homeFormation = null;
    let awayFormation = null;
    players.forEach((player) => {
        if (player.team_id == homeId) {
            homeFormation = player.formation;
        } else if (player.team_id == awayId) {
            awayFormation = player.formation;
        }
    });
    return { homeFormation, awayFormation };
}


export default function Game({ game_id = null, isCard = false, isModal = false }: GameProps) {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const [gameInfo, setGameInfo] = useState<GameInfo | null>(null);
    const [gameStats, setGameStats] = useState<any>(null);
    const [gamesHeadToHead, setGamesHeadToHead] = useState<GameInfo[]>([]);
    const [selectedHalf , setSelectedHalf] = useState<'full-game'|'first-half'|'second-half'>('full-game');
    const [refereeInfo, setRefereeInfo] = useState<RefereeInfo | null>(null);
    const gameId = game_id || id;
    const user = useUserStore((state) => state);

    useEffect(() => {
        fetchData('/api/fixture_info_by_id?id=' + gameId, (data) => {
            setGameInfo(data);
        }, user.premium);

        fetchData('/api/last_fixtures_head_to_head?id=' + gameId, (data) => {
            setGamesHeadToHead(data);
        }, user.premium);
    }, [gameId]);

    useEffect(() => {
        fetchDataAfterRefresh(`/api/fixture_statistics?fixture_ids=${gameId}&current_half=${selectedHalf}`, (data) => {
            setGameStats(data[0]);
        }, user.premium);
    }, [gameId, selectedHalf]);

    const [viewWidth, setViewWidth] = useState(window.innerWidth);

    useEffect(() => {
        const handleResize = () => {
            setViewWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);


    useEffect(() => {
        if (gameInfo && gameInfo.referee_id) {
            fetchDataAfterRefresh('/api/referee_by_id?referee_id=' + gameInfo.referee_id, (data) => {
                setRefereeInfo(data);
            }, false);
        }
    }, [gameInfo]);

    let pcWidth = 400;

    let formationWidth = viewWidth - 80;
    if (pcWidth * 2 < formationWidth) {
        formationWidth = pcWidth;
    }

    const isOnGamePage = !isModal && !isCard;


    return (
        <>
            <PremiumGuard>
                <SEO
                    title={gameInfo ? `${gameInfo?.home_name} vs ${gameInfo?.away_name} | Craque Stats` : t('game.game_1')}
                    description={t('game.game_2', { home: gameInfo?.home_name, away: gameInfo?.away_name, date: formatTimestampToLocalTime(gameInfo?.starting_at_timestamp || 0, { format: 'both' }) })}
                />
                <div className={`game-all ${isCard && 'game-as-card'} ${isModal && 'game-as-modal'} ${!isCard && !isModal && 'game-as-default'}`}>
                    {gameInfo && <div className="gap-1">
                        <div className={`game-names mb-2 gap-1 text-xl font-medium flex-wrap  ${(isCard || isModal) && 'justify-center'}`}>
                            <div className='mb-2 ms-0 float-end' style={{ display: 'block' }}>
                                <div className=" text-sm ">
                                    <Link to={`/league/${gameInfo.league_id}`} key={gameInfo.league_id} className="text-default-500 hover:text-primary font-medium items-center gap-2 max-w-sm me-3">
                                        <div className='mt-2'>
                                            <LeagueLogo leagueId={gameInfo.league_id} size={25} />
                                            <span className='ms-2 clickable-dashed'>{gameInfo.league_name}</span>
                                        </div>
                                    </Link>
                                    <br />
                                    {refereeInfo && (
                                        <Link to={`/referee/${refereeInfo.id}`} key={refereeInfo.id} className="text-default-500 hover:text-primary font-medium items-center gap-2 max-w-sm me-3">
                                            <Icon icon='mdi:whistle-outline' className='' />
                                            <span className='ms-2 clickable-dashed'>{refereeInfo.name}</span>
                                        </Link>
                                    )}
                                </div>

                            </div>
                            <div style={{ display: 'block' }} className='game-logos'>
                                <div className="block w-full">
                                    <Link to={`/team/${gameInfo.home_id}`} key={gameInfo.home_id} className="flex items-center gap-2 font-medium">
                                        <TeamLogo className='me-1' teamId={gameInfo.home_id} size={25} />
                                        <span className='text-center clickable-dashed'>{gameInfo.home_name}</span>
                                    </Link>
                                </div>
                                <div className="block w-full mt-3">
                                    <Link to={`/team/${gameInfo.away_id}`} key={gameInfo.away_id} className="flex items-center gap-2 font-medium">
                                        <TeamLogo className='me-1' teamId={gameInfo.away_id} size={25} />
                                        <span className='text-center clickable-dashed'>{gameInfo.away_name}</span>
                                    </Link>
                                </div>
                            </div>
                        </div>
                        <span className="text-default-500 text-sm mb-1 ms-5 text-right">
                            {formatTimestampToLocalTime(gameInfo.starting_at_timestamp, { format: 'both' })}
                        </span>
                    </div>}
                    <div className={!isOnGamePage ? '' : "flex flex-wrap w-full pt-5 px-3 gap-5"}>
                        
                        {gameInfo && <GamesStats gameInfo={gameInfo} gameStats={gameStats} 
                            selectedHalf={selectedHalf} setSelectedHalf={setSelectedHalf} />}
                        
                        {gameInfo && <GamesHeadToHead games={gamesHeadToHead} team1_id={gameInfo.home_id} team2_id={gameInfo.away_id} 
                            team1_name={gameInfo.home_name} team2_name={gameInfo.away_name}/>}

                        {isOnGamePage && gameInfo && <Field formationWidth={formationWidth} gameInfo={gameInfo} />}
                    </div>
                </div>
            </PremiumGuard>
        </>
    );
}
